import {
  GENERATE_PDF_REPORT_URL,
  GENERATE_MANAGED_REPORT_URL,
  LIST_PDF_REPORT_TEMPLATES_URL,
  LIST_REPORT_TEMPLATES_URL,
} from "@/utils/constants/constants";
import {
  normalize_admin_signature_payload,
  sections_require_admin_signature,
} from "@/utils/pdf_report_signature_helpers";
import axios from "axios";
import {
  PDF_TEMPLATE_KIND,
  extract_pdf_template_payload,
  extract_standard_template_payload,
  fetch_all_template_pages,
} from "./report_template_library_helpers";

export default {
  build_template_params(page) {
    const params = {
      project_id: this.project_id,
      is_active: true,
      search: this.search_query.trim() || undefined,
    };
    if (page) {
      params.page_number = page;
    }
    return params;
  },
  handle_template_error(error) {
    this.standard_templates = [];
    this.pdf_templates = [];
    this.show_error(error.response?.data?.message || "Failed to load templates");
  },
  fetch_standard_templates() {
    return fetch_all_template_pages({
      headers: this.headers,
      url: LIST_REPORT_TEMPLATES_URL,
      base_params: this.build_template_params(),
      extract_payload: extract_standard_template_payload,
    });
  },
  fetch_pdf_templates() {
    if (!this.include_pdf_templates) {
      return Promise.resolve([]);
    }
    return fetch_all_template_pages({
      headers: this.headers,
      url: LIST_PDF_REPORT_TEMPLATES_URL,
      base_params: this.build_pdf_template_params(),
      extract_payload: extract_pdf_template_payload,
    });
  },
  build_pdf_template_params() {
    const params = {
      project_id: this.project_id,
      is_active: true,
      search: this.search_query.trim() || undefined,
    };
    if (this.initial_entity_type_id_value) {
      params.entity_type_id = this.initial_entity_type_id_value;
    }
    return params;
  },
  run_template_request(page, loader = null) {
    return Promise.all([
      this.fetch_standard_templates(),
      this.fetch_pdf_templates(),
    ])
      .then(([standard_templates, pdf_templates]) => {
        this.standard_templates = standard_templates;
        this.pdf_templates = pdf_templates;
        this.page_number = this.get_next_page_number(page);
      })
      .catch((error) => {
        this.handle_template_error(error);
      })
      .finally(() => {
        this.loading_templates = false;
        this.searching_templates = false;
        if (!loader) {
          return null;
        }
        return this.close_loader(loader);
      });
  },
  load_templates(page = 1, use_loader = true) {
    this.loading_templates = true;
    if (!use_loader) {
      return this.run_template_request(page);
    }
    return this.show_loader("Loading report templates...")
      .then((loader) => this.run_template_request(page, loader));
  },
  get_target_entity_profile_id() {
    const parsed = Number(this.initial_target_entity_profile_id || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  get_initial_application_id() {
    const parsed = Number(this.initial_application_id || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  get_row_template(row) {
    return this.combined_templates.find(
      (template_record) =>
        template_record.id === row.id &&
        template_record.template_kind === row.template_type &&
        template_record.name === row.template_name
    ) || null;
  },
  resolve_template_record(row_or_template) {
    if (row_or_template?.template && row_or_template?.template_kind) {
      return row_or_template;
    }
    return this.get_row_template(row_or_template);
  },
  get_question_count(template) {
    return new Set(
      (template.filters || [])
        .filter((item) => item.section_question_id)
        .map((item) => item.section_question_id)
    ).size;
  },
  format_date(date_value) {
    if (!date_value) {
      return "Not available";
    }
    return new Date(date_value).toLocaleString();
  },
  format_entity_types(template) {
    const names = (template.entity_type_assignments || [])
      .map((assignment) => assignment.entity_type?.entity_type_name)
      .filter(Boolean);
    if (!names.length) {
      return this.translate_label("All entity types");
    }
    return names.join(", ");
  },
  format_pdf_entity_types(template) {
    const names = (template.entity_type_assignments || [])
      .map((assignment) => assignment.entity_type?.entity_type_name)
      .filter(Boolean);
    if (!names.length) {
      return this.translate_label("No entity types");
    }
    return names.join(", ");
  },
  format_pdf_generation_mode(generation_mode) {
    if (generation_mode === "on_status_transition") {
      return "On status transition";
    }
    return "Manual";
  },
  build_template_row_key(row) {
    return `${row.template_type}:${row.id}`;
  },
  get_next_page_number(requested_page_number) {
    const total_rows =
      (this.standard_templates?.length || 0) + (this.pdf_templates?.length || 0);
    const total_pages = Math.max(Math.ceil(total_rows / this.page_size), 1);
    return Math.min(requested_page_number, total_pages);
  },
  format_pdf_section_type(section_type) {
    return String(section_type || "")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },
  get_last_generated_status_label() {
    const status_value = this.last_generated_report?.status;
    if (status_value?.display_name) {
      return status_value.display_name;
    }
    return this.format_pdf_section_type(status_value || "completed");
  },
  get_column_label(column) {
    const default_label = this.translate_label(
      column.report_field?.display_name || "-"
    );
    if (!column.display_label) {
      return default_label;
    }
    if (column.display_label === column.report_field?.display_name) {
      return default_label;
    }
    return column.display_label;
  },
  format_filter_value(filter_value) {
    if (!filter_value) {
      return "-";
    }
    if (Array.isArray(filter_value.values)) {
      return filter_value.values.join(", ");
    }
    if (filter_value.value === true) {
      return "Yes";
    }
    if (filter_value.value === false) {
      return "No";
    }
    return JSON.stringify(filter_value);
  },
  format_question_type(question_type) {
    return String(question_type || "")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },
  translate_label(label) {
    return label;
  },
  quick_generate(row) {
    const template_record = this.get_row_template(row);
    if (!template_record) {
      this.show_error("The selected template could not be found.");
      return;
    }
    if (template_record.template_kind === PDF_TEMPLATE_KIND) {
      this.quick_generate_pdf(template_record, row);
      return;
    }
    this.generating_template_id = this.build_template_row_key(row);
    this.last_generated_report = null;
    this.show_loader("Generating report...")
      .then((loader) => axios.post(
        GENERATE_MANAGED_REPORT_URL,
        this.build_generate_payload(template_record.template),
        { headers: this.headers }
      )
        .then((response) => {
          this.last_generated_report = response.data.report;
          this.$emit("generated", response.data.report);
          this.show_success("Report generated successfully.");
        })
        .catch((error) => {
          if (error.response?.data?.report) {
            this.last_generated_report = error.response.data.report;
          }
          this.show_error(error.response?.data?.message || "Failed to generate report.");
        })
        .finally(() => {
          this.generating_template_id = null;
          return this.close_loader(loader);
        }));
  },
  quick_generate_pdf(template_record, row) {
    if (!this.get_target_entity_profile_id()) {
      this.show_info(
        "PDF templates generate for a specific profile. Open this library from Overview of Profile to quick-generate the PDF."
      );
      return;
    }
    if (
      sections_require_admin_signature(
        template_record?.template?.sections || []
      )
    ) {
      this.pending_pdf_template_record = { template_record, row };
      this.signature_modal_visible = true;
      return;
    }
    this.submit_quick_generate_pdf(template_record, row);
  },
  submit_quick_generate_pdf(template_record, row, admin_signature = null) {
    this.generating_template_id = this.build_template_row_key(row);
    this.last_generated_report = null;
    const payload = this.build_pdf_generate_payload(
      template_record.template,
      admin_signature
    );
    axios.post(
      GENERATE_PDF_REPORT_URL,
      payload,
      { headers: this.headers }
    )
      .then((response) => {
        this.last_generated_report = response.data.data || null;
        this.$emit("generated", response.data.data || null);
        this.show_success("PDF report generated successfully.");
      })
      .catch((error) => {
        this.last_generated_report = error.response?.data?.data || null;
        this.show_error(error.response?.data?.message || "Failed to generate PDF report.");
      })
      .finally(() => {
        this.generating_template_id = null;
      });
  },
  build_generate_payload(template) {
    const payload = {
      template_id: template.id,
      output_format_id: template.output_format?.id,
    };
    const target_entity_profile_id = this.get_target_entity_profile_id();
    if (target_entity_profile_id) {
      payload.target_entity_profile_id = target_entity_profile_id;
    }
    return payload;
  },
  build_pdf_generate_payload(template, admin_signature = null) {
    const payload = {
      template_id: template.id,
      entity_profile_id: this.get_target_entity_profile_id(),
    };
    const application_id = this.get_initial_application_id();
    if (application_id) {
      payload.application_id = application_id;
    }
    if (admin_signature?.image_data_url) {
      payload.admin_signature = admin_signature;
    }
    return payload;
  },
  close_signature_modal() {
    this.signature_modal_visible = false;
    this.pending_pdf_template_record = null;
  },
  confirm_signature(signature_payload) {
    const pending_pdf_template_record = this.pending_pdf_template_record;
    this.signature_modal_visible = false;
    this.pending_pdf_template_record = null;
    if (!pending_pdf_template_record) {
      return;
    }
    this.submit_quick_generate_pdf(
      pending_pdf_template_record.template_record,
      pending_pdf_template_record.row,
      normalize_admin_signature_payload(signature_payload)
    );
  },
  open_template_details(row) {
    this.selected_template = this.resolve_template_record(row);
  },
  close_template_details() {
    this.selected_template = null;
  },
  edit_template(row) {
    this.close_template_details();
    const template_record = this.resolve_template_record(row);
    if (!template_record) {
      return;
    }
    if (template_record.template_kind === PDF_TEMPLATE_KIND) {
      this.$router.push({
        path: "/generate_reports/",
        query: { pdf_template_id: template_record.id },
      });
      return;
    }
    this.$router.push({
      path: "/generate_reports/",
      query: { template_id: template_record.id },
    });
  },
  go_to_page(page_number) {
    if (!page_number || page_number < 1 || page_number > this.total_pages) {
      return;
    }
    this.load_templates(page_number);
  },
  schedule_search() {
    clearTimeout(this.search_timer);
    this.search_timer = setTimeout(() => {
      this.apply_search();
    }, 250);
  },
  apply_search() {
    const normalized = this.search_query.trim();
    if (normalized === this.last_applied_search) {
      return;
    }
    this.searching_templates = true;
    this.last_applied_search = normalized;
    this.load_templates(1, false);
  },
  build_quick_generate_label(row) {
    if (row.template_type !== PDF_TEMPLATE_KIND) {
      return "Quick Generate";
    }
    return this.get_target_entity_profile_id() ? "Generate PDF" : "Use In Profile";
  },
  is_quick_generate_disabled(row) {
    if (row.template_type === PDF_TEMPLATE_KIND) {
      return this.generating_template_id === this.build_template_row_key(row);
    }
    return this.generating_template_id === this.build_template_row_key(row);
  },
};
